// Parametric Panel Builder — add components, get an auto-generated blueprint + steel list.
import { profileSize } from "@/lib/blueprintCalc";

export const RAIL_PROFILES = ["40x40 SHS", "50x50 SHS", "65x65 SHS", "50x25 RHS", "65x35 RHS", "38NB Pipe"];
export const TUBE_PROFILES = ["16x16 SHS", "20x20 SHS", "25x25 SHS", "32x32 SHS", "16 OD Tube", "19 OD Tube", "25 OD Tube"];

export const PANEL_INFILL_OPTIONS = [
  { value: "tube_spike", label: "Diplomat — spike top security (vertical tube)" },
  { value: "tube_flat", label: "Flat top (vertical tube between rails)" },
  { value: "slat", label: "Slats (horizontal)" },
  { value: "louvre", label: "Louvre slats (angled — privacy + airflow)" },
  { value: "blade", label: "Blade fence (flat bar architectural)" },
  { value: "laser_cut", label: "Custom laser-cut sheet" },
  { value: "rolltop", label: "Roll top mesh" },
  { value: "anticlimb", label: "Suicide prevention / anti-climb (358 mesh)" },
  { value: "mesh", label: "Weldmesh panel" },
  { value: "sheet", label: "Solid sheet" },
  { value: "none", label: "Frame only" },
];

export const BLADE_ORIENTATIONS = [
  { value: "vertical", label: "Vertical blades" },
  { value: "horizontal", label: "Horizontal blades" },
];

export const PANEL_DEFAULTS = {
  kind: "panel", // panel | box (weatherproof enclosure)
  qty: 1,
  width_mm: 2400,
  height_mm: 1800,
  post_profile: "65x65 SHS",
  rail_profile: "40x40 SHS",
  rail_count: 2,
  mid_rails: 0,
  infill_type: "tube_spike",
  tube_profile: "25x25 SHS",
  tube_centres_mm: 50,
  spike_extension_mm: 150,
  slat_width_mm: 65,
  slat_gap_mm: 10,
  // Louvre
  louvre_width_mm: 100,
  louvre_gap_mm: 20,
  louvre_angle_deg: 45,
  // Blade
  blade_orientation: "vertical",
  blade_width_mm: 50,
  blade_thickness_mm: 6,
  blade_gap_mm: 20,
  // Laser cut
  laser_cut_preset: "botanical_leaf",
  laser_cut_design_name: "",
  laser_cut_upload_url: "",
  laser_cut_sheet_thickness_mm: 3,
  mesh_aperture_mm: 50,
  sheet_thickness_mm: 1.6,
  depth_mm: 600, // box only
  material: "steel",
  stock_length_mm: 6500,
};

const r1 = (n) => Math.round(n * 10) / 10;

function packBars(cuts, stock) {
  // cuts: [{len, qty}] same profile — simple per-length packing
  let bars = 0;
  for (const c of cuts) {
    if (c.len <= 0 || c.qty <= 0) continue;
    const per = Math.max(1, Math.floor(stock / c.len));
    bars += Math.ceil(c.qty / per);
  }
  return bars;
}

export function calcPanel(pIn) {
  const p = { ...PANEL_DEFAULTS, ...pIn };
  if (p.kind === "box") return calcBox(p);
  const qty = Math.max(1, parseInt(p.qty) || 1);
  const postSize = profileSize(p.post_profile);
  const railSize = profileSize(p.rail_profile);
  const tubeSize = profileSize(p.tube_profile);
  const W = p.width_mm, H = p.height_mm;
  const clearW = Math.max(0, W - 2 * postSize);
  const innerH = Math.max(0, H - 2 * railSize);
  const mat = p.material === "aluminium" ? "aluminium" : "steel";

  const cutList = [];
  const compliance = [];
  const railsPerPanel = Math.max(0, (parseInt(p.rail_count) || 0) + (parseInt(p.mid_rails) || 0));
  let nInfill = 0, actualCentres = 0, tubeLen = 0, weldPoints = railsPerPanel * 2;

  cutList.push({ item: `Post ${p.post_profile} (${mat})`, cut: `${H} mm`, qty: 2 * qty, spacing: "-", for: "left + right frame posts" });
  if (railsPerPanel > 0)
    cutList.push({ item: `Rail ${p.rail_profile} (${mat})`, cut: `${Math.round(clearW)} mm`, qty: railsPerPanel * qty, spacing: p.mid_rails > 0 ? "top / bottom + mid" : "top / bottom", for: "welded between posts" });

  if ((p.infill_type === "tube_spike" || p.infill_type === "tube_flat") && clearW > tubeSize) {
    const centres = Math.max(tubeSize + 5, p.tube_centres_mm);
    nInfill = Math.max(2, Math.floor((clearW - tubeSize) / centres) + 1);
    actualCentres = nInfill > 1 ? (clearW - tubeSize) / (nInfill - 1) : 0;
    if (p.infill_type === "tube_spike") {
      tubeLen = Math.round(H - railSize + (p.spike_extension_mm || 0));
      cutList.push({ item: `Vertical ${p.tube_profile} — SPIKE TOP (${mat})`, cut: `${tubeLen} mm`, qty: nInfill * qty,
        spacing: `${r1(actualCentres)} mm centres`, for: `press spike tops, extend ${p.spike_extension_mm} mm above top rail` });
      compliance.push({ level: "info", msg: `Diplomat spike-top security — ${nInfill} verticals per panel at ${r1(actualCentres)} mm centres, punched through top rail.` });
      if (H < 1800) compliance.push({ level: "warn", msg: `Security panel height ${H} mm — spike-top fencing is normally 1800 mm+.` });
    } else {
      tubeLen = Math.round(innerH);
      cutList.push({ item: `Vertical ${p.tube_profile} (${mat})`, cut: `${tubeLen} mm`, qty: nInfill * qty,
        spacing: `${r1(actualCentres)} mm centres`, for: "welded between top & bottom rails" });
    }
    const gap = actualCentres - tubeSize;
    if (gap > 100) compliance.push({ level: "warn", msg: `Gap between verticals ${r1(gap)} mm — exceeds 100 mm (pool/safety max).` });
    weldPoints += nInfill * 2;
  } else if (p.infill_type === "slat" && innerH > p.slat_width_mm) {
    nInfill = Math.max(1, Math.floor((innerH + p.slat_gap_mm) / (p.slat_width_mm + p.slat_gap_mm)));
    actualCentres = nInfill > 1 ? (innerH - nInfill * p.slat_width_mm) / (nInfill - 1) + p.slat_width_mm : 0;
    cutList.push({ item: `Slat ${p.slat_width_mm} mm (${mat}, horizontal)`, cut: `${Math.round(clearW)} mm`, qty: nInfill * qty,
      spacing: `${r1(Math.max(0, actualCentres - p.slat_width_mm))} mm gaps`, for: `${nInfill} slats per panel, evened over ${Math.round(innerH)} mm` });
    weldPoints += nInfill * 2;
  } else if (p.infill_type === "louvre" && innerH > p.louvre_width_mm) {
    const angleRad = ((p.louvre_angle_deg || 45) * Math.PI) / 180;
    const projected = p.louvre_width_mm * Math.cos(angleRad);
    const pitch = Math.max(1, projected + p.louvre_gap_mm);
    nInfill = Math.max(1, Math.floor(innerH / pitch));
    actualCentres = innerH / nInfill;
    cutList.push({
      item: `Louvre blade ${p.louvre_width_mm} mm (${mat}, angled ${p.louvre_angle_deg}°)`,
      cut: `${Math.round(clearW)} mm`, qty: nInfill * qty,
      spacing: `${r1(actualCentres)} mm pitch`,
      for: `${nInfill} louvres per panel @ ${p.louvre_angle_deg}° — fixed via internal side-brackets`,
    });
    cutList.push({
      item: `Louvre bracket set (${mat}) — laser cut, ${nInfill} slot(s) per side`,
      cut: `${Math.round(innerH)} mm`, qty: 2 * qty, spacing: "-",
      for: "welded to inside face of each side post — holds every blade at the fixed angle",
    });
    weldPoints += nInfill * 2 + 4;
    compliance.push({ level: "info", msg: `Louvre design — ${nInfill} blades per panel @ ${p.louvre_angle_deg}° angle · ${r1(actualCentres)} mm vertical pitch.` });
  } else if (p.infill_type === "blade") {
    if (p.blade_orientation === "horizontal") {
      nInfill = Math.max(1, Math.floor((innerH + p.blade_gap_mm) / (p.blade_width_mm + p.blade_gap_mm)));
      const gap = nInfill > 1 ? (innerH - nInfill * p.blade_width_mm) / (nInfill - 1) : 0;
      cutList.push({
        item: `Blade — ${p.blade_width_mm}×${p.blade_thickness_mm} mm flat bar (${mat}, horizontal)`,
        cut: `${Math.round(clearW)} mm`, qty: nInfill * qty,
        spacing: `${r1(gap)} mm gaps`, for: `${nInfill} horizontal blades per panel — architectural blade fence`,
      });
      actualCentres = p.blade_width_mm + gap;
    } else {
      nInfill = Math.max(1, Math.floor((clearW + p.blade_gap_mm) / (p.blade_width_mm + p.blade_gap_mm)));
      const gap = nInfill > 1 ? (clearW - nInfill * p.blade_width_mm) / (nInfill - 1) : 0;
      cutList.push({
        item: `Blade — ${p.blade_width_mm}×${p.blade_thickness_mm} mm flat bar (${mat}, vertical)`,
        cut: `${Math.round(innerH)} mm`, qty: nInfill * qty,
        spacing: `${r1(gap)} mm gaps`, for: `${nInfill} vertical blades per panel — architectural blade fence`,
      });
      actualCentres = p.blade_width_mm + gap;
    }
    weldPoints += nInfill * 2;
  } else if (p.infill_type === "laser_cut") {
    const designName = p.laser_cut_design_name || (p.laser_cut_preset === "custom" ? "Custom uploaded design" : (p.laser_cut_preset || "Custom design"));
    cutList.push({
      item: `Laser-cut sheet — ${designName} (${p.laser_cut_sheet_thickness_mm} mm ${mat})`,
      cut: `${Math.round(clearW)} × ${Math.round(innerH)} mm`, qty: qty, spacing: "-",
      for: "supply DXF/DWG to laser cutter — cut sheet fits inside frame, framed on all edges",
    });
    cutList.push({
      item: "Laser-cut design file (DXF / DWG)",
      cut: `${p.laser_cut_sheet_thickness_mm} mm sheet, ${mat}`,
      qty: 1, spacing: "-", for: `Design: ${designName}${p.laser_cut_upload_url ? " (client-supplied artwork)" : " (from CCFI preset library)"}`,
    });
    compliance.push({ level: "info", msg: `Laser-cut infill — supply the DXF export to your laser cutter. Confirm sheet thickness (${p.laser_cut_sheet_thickness_mm} mm) allows the fine detail in "${designName}".` });
    nInfill = 1;
  } else if (p.infill_type === "rolltop") {
    cutList.push({ item: `Roll top mesh panel (${p.mesh_aperture_mm} mm aperture)`, cut: `${Math.round(clearW)} × ${H} mm`, qty: qty,
      spacing: "-", for: "roll-formed top & bottom edge, clamp fixed" });
    nInfill = 1;
  } else if (p.infill_type === "anticlimb") {
    cutList.push({ item: "358 anti-climb mesh panel (76.2×12.7 mm)", cut: `${Math.round(clearW)} × ${H} mm`, qty: qty,
      spacing: "-", for: "concealed fixings — no exposed bolt heads on accessible face" });
    compliance.push({ level: "info", msg: "Anti-climb / suicide prevention — structural engineering certification required. CCFI will coordinate." });
    if (H < 2400) compliance.push({ level: "error", msg: `Panel height ${H} mm below the 2400 mm safety-barrier minimum.` });
    nInfill = 1;
  } else if (p.infill_type === "mesh") {
    cutList.push({ item: `Weldmesh panel (${p.mesh_aperture_mm} mm aperture)`, cut: `${Math.round(clearW)} × ${Math.round(innerH)} mm`, qty: qty, spacing: "-", for: "fixed inside frame" });
    nInfill = 1;
  } else if (p.infill_type === "sheet") {
    cutList.push({ item: `Sheet ${p.sheet_thickness_mm} mm (${mat})`, cut: `${Math.round(clearW)} × ${Math.round(innerH)} mm`, qty: qty, spacing: "-", for: "seam welded to frame" });
    nInfill = 1;
  }

  const steelCuts = { [p.post_profile]: [{ len: H, qty: 2 * qty }] };
  if (railsPerPanel > 0) (steelCuts[p.rail_profile] = steelCuts[p.rail_profile] || []).push({ len: clearW, qty: railsPerPanel * qty });
  if (tubeLen > 0) (steelCuts[p.tube_profile] = steelCuts[p.tube_profile] || []).push({ len: tubeLen, qty: nInfill * qty });
  let stockBars = 0, totalMm = 0;
  for (const cuts of Object.values(steelCuts)) {
    stockBars += packBars(cuts, p.stock_length_mm);
    for (const cu of cuts) totalMm += cu.len * cu.qty;
  }

  return {
    cut_list: cutList,
    compliance,
    layout: { clear_w_mm: Math.round(clearW), inner_h_mm: Math.round(innerH), n_infill: nInfill,
              actual_centres_mm: r1(actualCentres), post_size: postSize, rail_size: railSize, tube_size: tubeSize },
    totals: {
      total_m: r1((W * qty) / 1000),
      steel_m: r1(totalMm / 1000),
      panels: qty, posts: 2 * qty, centre_posts: 0, rails: railsPerPanel * qty,
      pickets: (p.infill_type === "tube_spike" || p.infill_type === "tube_flat") ? nInfill * qty : 0,
      slats: p.infill_type === "slat" ? nInfill * qty : 0,
      louvres: p.infill_type === "louvre" ? nInfill * qty : 0,
      blades: p.infill_type === "blade" ? nInfill * qty : 0,
      laser_sheets: p.infill_type === "laser_cut" ? qty : 0,
      mesh_panels: ["rolltop", "anticlimb", "mesh"].includes(p.infill_type) ? qty : 0,
      gates: 0, stock_bars: stockBars, weld_points: weldPoints * qty,
    },
  };
}

function calcBox(p) {
  const qty = Math.max(1, parseInt(p.qty) || 1);
  const f = profileSize(p.rail_profile);
  const W = p.width_mm, H = p.height_mm, D = p.depth_mm;
  const mat = p.material === "aluminium" ? "aluminium" : "steel";
  const cutList = [
    { item: `Frame ${p.rail_profile} — verticals (${mat})`, cut: `${H} mm`, qty: 4 * qty, spacing: "-", for: "4 corner uprights" },
    { item: `Frame ${p.rail_profile} — width members`, cut: `${Math.max(0, W - 2 * f)} mm`, qty: 4 * qty, spacing: "-", for: "top + bottom, front + back" },
    { item: `Frame ${p.rail_profile} — depth members`, cut: `${Math.max(0, D - 2 * f)} mm`, qty: 4 * qty, spacing: "-", for: "top + bottom, both sides" },
    { item: `Sheet ${p.sheet_thickness_mm} mm — sides`, cut: `${D} × ${H} mm`, qty: 2 * qty, spacing: "-", for: "seam welded, weatherproof" },
    { item: `Sheet ${p.sheet_thickness_mm} mm — back`, cut: `${W} × ${H} mm`, qty: qty, spacing: "-", for: "seam welded" },
    { item: `Sheet ${p.sheet_thickness_mm} mm — top`, cut: `${W} × ${D} mm`, qty: qty, spacing: "-", for: "5° fall to rear, drip edge" },
    { item: `Sheet ${p.sheet_thickness_mm} mm — door`, cut: `${W} × ${H} mm`, qty: qty, spacing: "-", for: "hinged front door + weather seal" },
    { item: "Hinges — stainless (pair)", cut: "-", qty: qty, spacing: "-", for: "door" },
    { item: "Key-lockable latch", cut: "-", qty: qty, spacing: "-", for: "door" },
    { item: "Breather vent (weatherproof)", cut: "-", qty: 2 * qty, spacing: "-", for: "condensation control" },
  ];
  const frameMm = (4 * H + 4 * (W - 2 * f) + 4 * (D - 2 * f)) * qty;
  return {
    cut_list: cutList,
    compliance: [
      { level: "info", msg: "Weatherproof enclosure — seal all seams; IP-rated glands for cable entries." },
      { level: "info", msg: "If housing 240V equipment (EV charger / gate motor), licensed electrician required for connection. CCFI will coordinate." },
    ],
    layout: { clear_w_mm: W, inner_h_mm: H, n_infill: 0, actual_centres_mm: 0, post_size: f, rail_size: f, tube_size: 0 },
    totals: {
      total_m: r1((W * qty) / 1000), steel_m: r1(frameMm / 1000), panels: qty, posts: 4 * qty, centre_posts: 0,
      rails: 8 * qty, pickets: 0, slats: 0, mesh_panels: 0, gates: 0,
      stock_bars: packBars([{ len: H, qty: 4 * qty }, { len: W - 2 * f, qty: 4 * qty }, { len: D - 2 * f, qty: 4 * qty }], p.stock_length_mm),
      weld_points: 24 * qty,
    },
  };
}

// ---- Member-by-member builder: add one component at a time, blueprint assembles itself ----
export const MEMBER_KINDS = [
  { value: "post", label: "Post (vertical)" },
  { value: "rail", label: "Rail (horizontal)" },
  { value: "slat", label: "Slat" },
  { value: "louvre", label: "Louvre blade (angled)" },
  { value: "blade", label: "Blade (flat bar)" },
  { value: "tube", label: "Infill tube (vertical)" },
  { value: "brace", label: "Brace (diagonal)" },
  { value: "laser_cut", label: "Laser-cut sheet" },
  { value: "custom", label: "Custom member" },
];
export const MEMBER_POSITIONS = {
  post: [{ value: "left", label: "Left" }, { value: "right", label: "Right" }, { value: "centre", label: "Centre" }],
  rail: [{ value: "top", label: "Top" }, { value: "bottom", label: "Bottom" }, { value: "mid", label: "Mid" }],
  slat: [{ value: "horizontal", label: "Horizontal" }, { value: "vertical", label: "Vertical" }],
  louvre: [{ value: "horizontal", label: "Horizontal (angled)" }],
  blade: [{ value: "vertical", label: "Vertical" }, { value: "horizontal", label: "Horizontal" }],
};
export const ALL_PROFILES = ["25x25 SHS", "30x30 SHS", "40x40 SHS", "50x50 SHS", "65x65 SHS", "75x75 SHS", "89x89 SHS", "100x100 SHS", "50x25 RHS", "65x35 RHS", "65x16 Slat", "100x16 Louvre", "50x6 Flat bar", "65x6 Flat bar", "75x8 Flat bar", "16x16 SHS", "20x20 SHS", "16 OD Tube", "19 OD Tube", "25 OD Tube", "40NB Pipe", "50NB Pipe", "80NB Pipe", "3mm Laser sheet", "5mm Laser sheet"];

const KIND_LABEL = { post: "Post", rail: "Rail", slat: "Slat", louvre: "Louvre", blade: "Blade", tube: "Infill tube", brace: "Brace", laser_cut: "Laser-cut sheet", custom: "Member" };

export function calcMembers(membersIn, stock = 6500) {
  const members = (membersIn || []).filter((m) => (m.length_mm || 0) > 0 && (m.qty || 0) > 0);
  const cutList = members.map((m) => {
    const spacing = m.gap_mm > 0 && m.member_width_mm > 0
      ? `${m.member_width_mm} mm wide @ ${Math.round(m.member_width_mm + m.gap_mm)} mm pitch (${m.gap_mm} mm gap)`
      : "-";
    return {
      item: `${KIND_LABEL[m.kind] || "Member"}${m.position ? ` (${m.position})` : ""} ${m.profile} (${m.material || "steel"})`,
      cut: `${Math.round(m.length_mm)} mm`, qty: parseInt(m.qty) || 1, spacing,
      for: m.note || m.position || "as detailed",
    };
  });
  const byProfile = {};
  let totalMm = 0, posts = 0, rails = 0, tubes = 0, slats = 0, louvres = 0, blades = 0, laserSheets = 0, welds = 0;
  for (const m of members) {
    const q = parseInt(m.qty) || 1;
    (byProfile[`${m.profile}|${m.material}`] = byProfile[`${m.profile}|${m.material}`] || []).push({ len: m.length_mm, qty: q });
    totalMm += m.length_mm * q;
    if (m.kind === "post") posts += q;
    else if (m.kind === "rail") rails += q;
    else if (m.kind === "tube") tubes += q;
    else if (m.kind === "slat") slats += q;
    else if (m.kind === "louvre") louvres += q;
    else if (m.kind === "blade") blades += q;
    else if (m.kind === "laser_cut") laserSheets += q;
    welds += q * 2;
  }
  let stockBars = 0;
  for (const cuts of Object.values(byProfile)) stockBars += packBars(cuts, stock);
  const horiz = members.filter((m) => m.kind === "rail" || m.kind === "louvre" || ((m.kind === "slat" || m.kind === "blade") && m.position !== "vertical"));
  const vert = members.filter((m) => m.kind === "post" || m.kind === "tube" || ((m.kind === "slat" || m.kind === "blade") && m.position === "vertical"));
  return {
    cut_list: cutList, compliance: [],
    layout: { width_mm: Math.max(0, ...horiz.map((m) => m.length_mm)) || 2400,
              height_mm: Math.max(0, ...vert.map((m) => m.length_mm)) || 1800 },
    totals: {
      total_m: Math.round(totalMm / 100) / 10, steel_m: Math.round(totalMm / 100) / 10,
      panels: 1, posts, centre_posts: 0, rails, pickets: tubes, slats, louvres, blades, laser_sheets: laserSheets, mesh_panels: 0,
      gates: 0, stock_bars: stockBars, weld_points: welds,
    },
  };
}
